import type { CamelCasedPropertiesDeep } from "type-fest";
import {
  getUserImageUrl,
  recursiveCamelCaseCipherTypesafe,
} from "../../postgrestApi/common";
import FetchClients, { ClientOutput } from "../client";
import { throwBackpackResponseError } from "../errors";

export const CONTROL_PANEL_SITES_KEY = "sites-rollup-data";
export const CONTROL_PANEL_ACCOUNT_SUMMARY_KEY = "account-rollup-data";

/** A property note with the avatar URL derived from the author's `imageKey`. */
export type PropertyNoteType = CamelCasedPropertiesDeep<
  ClientOutput["controlPanel"]["ControlPanelSiteNote"]
> & { imageUrl: string | null };

export type ControlPanelSitesData = Omit<
  CamelCasedPropertiesDeep<ClientOutput["controlPanel"]["ControlPanelSite"]>,
  "notes"
> & { notes: PropertyNoteType[] };

export type ControlPanelAccountSummary = CamelCasedPropertiesDeep<
  ClientOutput["controlPanel"]["ControlPanelAccountSummary"]
>;

export type AccountSummaryTypes = keyof ControlPanelAccountSummary;

export type NotesMap = {
  [siteId: number]: PropertyNoteType[];
};

const controlPanel = {
  getControlPanelInfo,
  getControlPanelAccountSummary,
} as const;

export default controlPanel;

/**
 * Sorts by onboarding completion, descending. Sites with no onboarding guide sort
 * first, matching Postgres' NULLS FIRST default for `ORDER BY ... DESC` which the
 * PostgREST view this replaced relied on.
 */
function compareByOnboardingPercentDesc(
  siteA: ControlPanelSitesData,
  siteB: ControlPanelSitesData
) {
  const percentA = siteA.onboardingGuidePercentComplete;
  const percentB = siteB.onboardingGuidePercentComplete;
  const siteAUnstarted = percentA === null || percentA === undefined;
  const siteBUnstarted = percentB === null || percentB === undefined;
  if (siteAUnstarted || siteBUnstarted) {
    return Number(siteBUnstarted) - Number(siteAUnstarted);
  }
  return percentB - percentA;
}

/**
 * Oldest first. The endpoint documents no ordering for the aggregated notes, and
 * the panel appends a newly saved note to the end, so the order is pinned here.
 */
function compareByCreatedAtAsc(
  noteA: PropertyNoteType,
  noteB: PropertyNoteType
) {
  return (
    new Date(noteA.createdAt).getTime() - new Date(noteB.createdAt).getTime()
  );
}

/** Onboarding completion rows for every active site on an account. */
export async function getControlPanelInfo(accountId: number) {
  const { data, error } = await FetchClients.controlPanel.GET("", {
    params: { query: { account_id: accountId } },
  });
  if (data) {
    return recursiveCamelCaseCipherTypesafe(data)
      .map((site) => ({
        ...site,
        notes: site.notes
          .map((note) => ({
            ...note,
            imageUrl: getUserImageUrl(note.imageKey),
          }))
          .sort(compareByCreatedAtAsc),
      }))
      .sort(compareByOnboardingPercentDesc);
  }
  throwBackpackResponseError(error);
}

/**
 * Account-level onboarding counts backing the status cards. The endpoint returns the
 * single summary row wrapped in an array, as the PostgREST view it replaced did.
 */
export async function getControlPanelAccountSummary(accountId: number) {
  const { data, error } = await FetchClients.controlPanel.GET("/summary", {
    params: { query: { account_id: accountId } },
  });
  if (data) {
    const [summary] = data;
    return summary ? recursiveCamelCaseCipherTypesafe(summary) : undefined;
  }
  throwBackpackResponseError(error);
}
